import type { LoadMode } from './config';
import type { DicomWebClient, InstanceRef, SeriesRef } from './dicomweb';
import { installGeometryProvider, registerEnhancedFrame } from './geometry-provider';
import { isEnhancedMultiframe } from './framegeometry';
import { jpegFrameImageId } from './jpegframes';
import { decodedImageProblem, type DecodedImage } from './decodecheck';
import { isMobileDevice } from './device';
import { registerLutProvider } from './display/voilut-cs';
import { watchXhrIdle } from './net';

/** Same cache ceiling as the IMEXHS viewer (imageCache.setMaximumSizeBytes(4294967296)). */
const DESKTOP_IMAGE_CACHE_BYTES = 4 * 1024 ** 3;
/**
 * Phones and tablets: iOS Safari kills a tab long before 4 GiB of decoded pixels (IMEXHS v8 also
 * drops to 512 MiB on touch devices). The cache evicts least-recently-used images past the ceiling.
 */
const MOBILE_IMAGE_CACHE_BYTES = 512 * 1024 ** 2;

export function imageCacheBytes(mobile = isMobileDevice()): number {
  return mobile ? MOBILE_IMAGE_CACHE_BYTES : DESKTOP_IMAGE_CACHE_BYTES;
}

/** IMEXHS WADOLoaderConfig: maxWebWorkers 4, started on demand, codecs initialised on start. */
const PARITY_MAX_WORKERS = 4;

/** Max decode workers in fast mode; more than this only adds start-up cost and memory. */
const FAST_MAX_WORKERS = 8;

/** IMEXHS sends these on every WADO request, which defeats any HTTP cache (see spec section 5). */
const PARITY_NO_CACHE_HEADERS = { 'Cache-Control': 'no-store', Pragma: 'no-cache' } as const;

/** Modalities IMEXHS opens on the first image; every other series opens on the middle slice. */
const OPEN_ON_FIRST_IMAGE = new Set(['XA', 'US', 'DX', 'CR']);

/**
 * Parallel retrievals per priority in fast mode. HTTP/2 multiplexes these on one connection,
 * so the browser's 6-per-host HTTP/1.1 limit does not apply.
 */
const FAST_RETRIEVAL_LIMITS = { interaction: 12, thumbnail: 4, prefetch: 12 } as const;

export interface LoaderContext {
  mode: LoadMode;
  wadoUriRoot: string;
  client: DicomWebClient;
}

/**
 * Wires the pinned legacy stack together and sets the image-loading strategy.
 *
 * parity reproduces the IMEXHS viewer: canvas renderer, 4 GiB cache, 4 decode workers started on demand,
 * lenient decoding, no-store request headers (so nothing is ever served from the HTTP cache).
 * fast keeps the same libraries and adds: every worker started and its codecs initialised at boot
 * (the first image no longer pays worker + wasm start-up), higher retrieval concurrency, and WADO-RS frames
 * in their stored transfer syntax instead of whole Part-10 files.
 */
export function initImageLoading(ctx: LoaderContext): void {
  const { cornerstone, cornerstoneTools, cornerstoneWADOImageLoader, cornerstoneMath, dicomParser, Hammer } = window;
  cornerstoneWADOImageLoader.external.cornerstone = cornerstone;
  cornerstoneWADOImageLoader.external.dicomParser = dicomParser;
  cornerstoneTools.external.cornerstone = cornerstone;
  cornerstoneTools.external.cornerstoneMath = cornerstoneMath;
  cornerstoneTools.external.Hammer = Hammer;

  cornerstone.imageCache.setMaximumSizeBytes(imageCacheBytes());
  installGeometryProvider();
  registerLutProvider();

  // Read on every request: under a hub session the ticket is renewed while the viewer is open.
  const requestHeaders = (): Record<string, string> => ({
    ...(ctx.mode === 'parity' ? PARITY_NO_CACHE_HEADERS : {}),
    ...ctx.client.headers(),
  });
  cornerstoneWADOImageLoader.configure({
    strict: false,
    beforeSend: (xhr) => {
      for (const [name, value] of Object.entries(requestHeaders())) xhr.setRequestHeader(name, value);
      watchXhrIdle(xhr);
    },
  });

  guardDecodedImages();

  const hardware = navigator.hardwareConcurrency || 1;
  if (ctx.mode === 'parity') {
    cornerstoneWADOImageLoader.webWorkerManager.initialize({
      maxWebWorkers: PARITY_MAX_WORKERS,
      startWebWorkersOnDemand: true,
      taskConfiguration: { decodeTask: { initializeCodecsOnStartup: true, strict: false } },
    });
    return;
  }

  cornerstoneWADOImageLoader.webWorkerManager.initialize({
    maxWebWorkers: Math.min(hardware, FAST_MAX_WORKERS),
    startWebWorkersOnDemand: false,
    taskConfiguration: { decodeTask: { initializeCodecsOnStartup: true, strict: false } },
  });
  Object.assign(cornerstone.imageRetrievalPoolManager.maxNumRequests, FAST_RETRIEVAL_LIMITS);
}

type ImageLoadObject = { promise: Promise<unknown>; cancelFn?: () => void };
type ImageLoader = (imageId: string, options?: unknown) => ImageLoadObject;

/** Re-registers the DICOM loaders so an image that decodes to nothing is a failure (see decodecheck.ts). */
function guardDecodedImages(): void {
  const wado = window.cornerstoneWADOImageLoader as unknown as { wadors: { loadImage: ImageLoader }; wadouri: { loadImage: ImageLoader } };
  const core = window.cornerstone as unknown as { registerImageLoader(scheme: string, loader: ImageLoader): void };
  const guarded = (load: ImageLoader): ImageLoader => (imageId, options) => {
    const loadObject = load(imageId, options);
    return {
      ...loadObject,
      promise: loadObject.promise.then((image) => {
        const problem = decodedImageProblem(image as DecodedImage);
        if (problem) throw new Error(`decoded image unusable (${problem}): ${imageId.split('?')[0]}`);
        return image;
      }),
    };
  };
  core.registerImageLoader('wadors', guarded(wado.wadors.loadImage));
  for (const scheme of ['wadouri', 'dicomweb']) core.registerImageLoader(scheme, guarded(wado.wadouri.loadImage));
}

/** Index the series opens on: first image for XA/US/DX/CR, otherwise the middle one (as IMEXHS does). */
export function initialImageIndex(modality: string, imageCount: number): number {
  return OPEN_ON_FIRST_IMAGE.has(modality) ? 0 : Math.floor(imageCount / 2);
}

/** One imageId per frame, in display order. Fast mode also hands the loader the frame's metadata. */
export function imageIdsForSeries(ctx: LoaderContext, series: SeriesRef): string[] {
  const ids: string[] = [];
  for (const instance of series.instances) {
    for (let frame = 1; frame <= instance.numberOfFrames; frame++) {
      ids.push(ctx.mode === 'fast' ? wadorsImageId(ctx, series, instance, frame) : wadouriImageId(ctx, series, instance, frame));
    }
  }
  return ids;
}

function wadorsImageId(ctx: LoaderContext, series: SeriesRef, instance: InstanceRef, frame: number): string {
  const imageId = `wadors:${absolute(ctx.client.frameUrl(series, instance, frame))}`;
  // A shallow copy per frame: the loader's combineFrameInstance (4.13.2) writes the merged frame attributes INTO
  // the object it was given, which would otherwise rewrite the instance metadata every other module reads.
  const metadata = instance.metadata ? { ...instance.metadata } : instance.metadata;
  window.cornerstoneWADOImageLoader.wadors.metaDataManager.add(imageId, metadata);
  if (instance.metadata && isEnhancedMultiframe(instance.metadata)) registerEnhancedFrame(imageId, instance.metadata, frame);
  return imageId;
}

function wadouriImageId(ctx: LoaderContext, series: SeriesRef, instance: InstanceRef, frame: number): string {
  // --- parity multi-frame as JPEG frames (IMEXHS forceRasterMF), owned by jpegframes.ts ---
  if (instance.numberOfFrames > 1) return jpegFrameImageId(ctx, series, instance, frame);
  // --- end parity multi-frame ---
  const params = new URLSearchParams({
    requestType: 'WADO',
    studyUID: series.studyInstanceUID,
    seriesUID: series.seriesInstanceUID,
    objectUID: instance.sopInstanceUID,
    contentType: 'application/dicom',
  });
  const frameSuffix = instance.numberOfFrames > 1 ? `&frame=${frame - 1}` : '';
  return `wadouri:${absolute(ctx.wadoUriRoot)}?${params.toString()}${frameSuffix}`;
}

function absolute(url: string): string {
  return new URL(url, window.location.href).toString();
}
